import { BooleanControl } from "./playgroundControls";
import {
    isPickerPlaygroundTextTrigger,
    type PickerPlaygroundFeatures,
} from "./PickerPlayground.lib";
import { PickerPlaygroundOptionControls } from "./PickerPlaygroundOptionControls";
import styles from "./DatePickerPlayground.module.scss";

type PickerBooleanFeature = Exclude<
    keyof PickerPlaygroundFeatures,
    | "status"
    | "trigger"
    | "buttonVariant"
    | "summaryMode"
    | "emptyBehavior"
    | "nested"
    | "parentSelection"
    | "flyoutSearch"
    | "flyoutFooter"
    | "flyoutQueryScope"
    | "flyoutAutoOpen"
    | "chevron"
>;

const featureControls: ReadonlyArray<{
    key: PickerBooleanFeature;
    label: string;
    /** Extra visibility rule for the toggle. */
    isVisible?: (features: PickerPlaygroundFeatures) => boolean;
}> = [
    {
        key: "isMultiple",
        label: "Multiple",
        isVisible: (features) => !features.allowsCustomValue,
    },
    {
        key: "isSearchable",
        label: "Searchable",
        // A text trigger is the search; the toggle would be a no-op.
        isVisible: (features) =>
            !isPickerPlaygroundTextTrigger(features.trigger),
    },
    {
        key: "isFilterable",
        label: "Filterable (text triggers)",
        isVisible: (features) =>
            isPickerPlaygroundTextTrigger(features.trigger),
    },
    {
        key: "allowsCustomValue",
        label: "Allow custom value",
        isVisible: (features) =>
            isPickerPlaygroundTextTrigger(features.trigger) &&
            !features.isMultiple,
    },
    {
        key: "hasSections",
        label: "Sections with select-all",
    },
    {
        key: "hasFooter",
        label: "Footer (Add ...)",
    },
    {
        key: "isTree",
        label: "Tree items",
    },
    {
        key: "hasSelectableParents",
        label: "Selectable parents",
        isVisible: (features) =>
            features.isTree &&
            !(features.isMultiple && features.parentSelection === "cascade"),
    },
    {
        key: "isExpandedByDefault",
        label: "Expand all (tree)",
        isVisible: (features) => features.isTree && features.nested === "tree",
    },
    {
        key: "isPaged",
        label: "Paged (load more)",
    },
    {
        key: "isNextPageFailing",
        label: "Fail next page",
    },
    {
        key: "isClearable",
        label: "Clearable",
    },
    {
        key: "isEmpty",
        label: "Empty state",
    },
    {
        key: "hasDisabledItems",
        label: "Disabled item",
    },
    {
        key: "hasDestructiveItem",
        label: "Destructive item",
    },
    {
        key: "hasDescriptions",
        label: "Descriptions",
    },
    {
        key: "hasRightText",
        label: "Right text",
    },
    { key: "isDisabled", label: "Disabled" },
];

/** Toggles that stop the rows being one flat list, so paging cannot combine with them. */
const pagedConflicts: readonly PickerBooleanFeature[] = [
    "hasSections",
    "isTree",
    "isEmpty",
    "hasDisabledItems",
    "hasDestructiveItem",
    "allowsCustomValue",
];

const hasPagedConflict = (features: PickerPlaygroundFeatures) =>
    pagedConflicts.some((key) =>
        key === "allowsCustomValue"
            ? features.allowsCustomValue &&
              isPickerPlaygroundTextTrigger(features.trigger)
            : features[key],
    );

/** Paging searches on the server, so client filtering is off too. */
const pagedDisabledFeatures: readonly PickerBooleanFeature[] = [
    ...pagedConflicts,
    "isFilterable",
];

/**
 * The Picker sandbox aside: the option groups (trigger, status, summary,
 * nesting, parent selection, chevron, empty behaviour) and the boolean
 * feature toggles.
 */
export const PickerPlaygroundControls = ({
    features,
    onFeaturesChange,
}: {
    features: PickerPlaygroundFeatures;
    onFeaturesChange: (features: PickerPlaygroundFeatures) => void;
}) => {
    const isBooleanDisabled = (key: PickerBooleanFeature) =>
        (key === "hasSections" && features.isEmpty) ||
        (key === "isTree" && features.isEmpty) ||
        (key === "isPaged" && hasPagedConflict(features)) ||
        (key === "isNextPageFailing" && !features.isPaged) ||
        (features.isPaged && pagedDisabledFeatures.includes(key));

    return (
        <>
            <PickerPlaygroundOptionControls
                features={features}
                onFeaturesChange={onFeaturesChange}
            />
            <section>
                <h2>Features</h2>
                <div className={styles.booleanGrid}>
                    {featureControls
                        .filter(
                            ({ isVisible }) => isVisible?.(features) ?? true,
                        )
                        .map(({ key, label }) => (
                            <BooleanControl
                                key={key}
                                label={label}
                                checked={features[key]}
                                disabled={isBooleanDisabled(key)}
                                onChange={(checked) =>
                                    onFeaturesChange({
                                        ...features,
                                        [key]: checked,
                                        // The engine default for parents follows `multiple`.
                                        ...(key === "isMultiple" && {
                                            hasSelectableParents: checked,
                                        }),
                                        ...(key === "isPaged" && {
                                            isNextPageFailing: false,
                                        }),
                                    })
                                }
                            />
                        ))}
                </div>
            </section>
        </>
    );
};
